/**
 * 地域文脈 + 要約 + 構造化表 を統合した生成AI向けプロンプトを組み立てる
 *
 * 外部にコピーされる本文からは、プロトコルID・研究用ラベル・開発環境の情報を除く（コード内定数のみ保持可）。
 */

import {
  getLocationContext,
  getMonthlyContext,
  getTakayamaBase,
} from './takayamaContext';

/** 研究ログ等で条件を指す用（コピー用プロンプト本文には出さない） */
export const ADVISER_PROTOCOL_ID = 'Takayama-ShoppingStreet-Adviser-Protocol-v2';

/** 本番の利用者向け公開オリジン（README / sitemap と揃える） */
export const PRODUCTION_DASHBOARD_ORIGIN = 'https://ai-camera.lab.mdg-meidai.com';

/**
 * 一般利用者向けに公開しているサービスURLのみ（開発ホストやコピー元URLは載せない）
 */
function formatPublicServiceLine(lang) {
  if (lang === 'en') {
    return [
      '【Service】',
      `- Public dashboard: ${PRODUCTION_DASHBOARD_ORIGIN}`,
    ].join('\n');
  }
  return [
    '【サービス】',
    `- 一般公開のダッシュボード: ${PRODUCTION_DASHBOARD_ORIGIN}`,
  ].join('\n');
}

/**
 * プロンプト用: URLから目的ベース／グラフベースを判定
 * @param {string} [pathname]
 * @returns {'purpose'|'function'|'other'}
 */
export function detectDashboardUiMode(pathname) {
  const p = String(pathname || '');
  if (p.includes('/function')) return 'function';
  if (p.includes('/purpose')) return 'purpose';
  return 'other';
}

/**
 * コピー用テキスト向け: 役割・注意・トーン（研究用IDや内部ラベルは含めない）
 */
function buildMerchantGuideBlock(lang) {
  if (lang === 'en') {
    return [
      '【Role & ground rules】',
      'You advise merchants in Takayama shopping streets. Read Summary + Tables faithfully, then propose realistic actions and concrete verification steps—not only cautious explanation.',
      '【Keep answers trustworthy】',
      '- Do not invent numbers that are not in the Summary or Tables.',
      '- Do not treat “Location/Month” regional blurbs as numeric proof for the selected month.',
      '- Whenever you cite a date or weekday aggregate, make it clear what is already measured vs what is a forward-looking estimate; add a short plain-language gloss in the same sentence when helpful.',
      '- When suggesting the next screen, use the exact menu strings and the Action-First / Data-First toggle described in the Interface specification block.',
      '【Merchant reality】',
      '- Merchants juggle sales, staffing, stamina, local events, and coordination with neighbors. Congestion here is only estimated street foot traffic at the chosen point.',
      '- Separate: (1) what the congestion numbers support, (2) what the shop should verify outside this dashboard.',
      '【Communication tone】',
      '- Prefer everyday wording over jargon; write for merchants who may not be comfortable with statistics.',
      '- If you use terms like actual vs forecast, standard deviation, or “n=… days”, add a short plain-language gloss in the same sentence (what it means for opening/closing decisions).',
      '- Lettered (a)(b)(c) labels are not required; natural paragraphs or bullets are fine as long as measured vs estimate stays clear.',
      '- Favor suggestions (“you could…”, “consider checking…”) over commands (“you must…”).',
      '- Make at least one clear next step obvious (on the dashboard or in the shop).',
      '- You may use the web for trustworthy Takayama context; label dashboard vs web; never fabricate metrics.',
    ].join('\n');
  }
  return [
    '【役割と注意（利用者向け）】',
    'あなたは岐阜県高山市の商店街事業者の意思決定を支援するアドバイザーです。データを正しく読んだうえで、現実に動ける行動案と、店・画面上で確認すべき次の一歩まで一緒に考えてください（無難な説明だけで終えない）。',
    '【信頼できる回答のために】',
    '- 「要約」「表」にない数値を新たに作らない。',
    '- 「地域文脈」の地点・時期の定型文を、この月の数値根拠や通行量の確定証拠として扱わない。',
    '- 日付・曜日集計の数値を引用するときは、その都度「すでに計測された日の値」か「先の日の見込み」かが分かるように書く（アプリでは実績／予測と表記されることがあります）。',
    '- 次に開く画面を案内するときは【インターフェース仕様】のメニュー文言のままとし、「目的ベースへ／グラフベースへ」の切替を具体的に書く（アプリに無い画面名を作らない）。',
    '【事業者の現実と前提】',
    '- 商店街の事業者は、売上だけでなくスタッフの体力・家族の時間・地域行事・他店との足並みなど、複数の制約のなかで「ベター」を選んでいる。',
    '- このダッシュボードの混雑度は「通りの歩行者推計」の一部のみを示し、店内売上や客単価そのものではない。',
    '- 助言では、(1) 混雑データから言える候補、(2) 店側でデータ外として確認すべき点、を分けて述べる。',
    '【コミュニケーションのトーン】',
    '- 専門用語より、商店街の日常会話に近い言葉を優先する。データ分析に不慣れな事業者にも読める文章にする。',
    '- 「実績」「予測」「標準偏差」「◯日分」などを使う場合は、同じ文か直後に一言で言い換える。',
    '- (a)(b)(c) の記号や形式的な見出しは必須としない。自然な日本語の段落・箇条書きでよいが、計測済みか見込みかの区別は文中で必ず伝わること。',
    '- 「〜すべき」の断定より、「〜という案が考えられます」「〜も一度確認すると安心です」のような提案・確認を促す言い方を優先する。',
    '- 回答のどこかで「次に何をすればよいか」が一つ以上はっきり伝わるようにする。',
    '- 必要なら信頼できるウェブ情報で高山の事情を補ってよいが、埋め込みデータとウェブ由来を区別し、数値の捏造はしない。',
  ].join('\n');
}

/**
 * 初見のモデル向け: この貼り付けテキストの性質・読み順・用語
 */
function formatPromptDocumentGuide(lang) {
  if (lang === 'en') {
    return [
      '【How to read this pasted document (for the model)】',
      '- This is a single self-contained prompt copied from a Takayama pedestrian-camera congestion dashboard. It bundles: the user’s current menu choice, location, period, auto-generated bullet summaries and Markdown tables, short regional notes, and methodology. It is not a chat transcript.',
      '- Reading order: (1) Role & ground rules (2) This guide (3) Dashboard UI premise (4) Interface specification (5) Screen snapshot (consultation → on-screen state → service line → regional → events → summary → tables → congestion notes) (6) Instructions. Quantitative evidence is only Summary + Tables inside the snapshot; regional lines are qualitative; use (4) before naming the next screen; use congestion notes before over-reading decimals or “prediction” cells.',
      '- “Congestion” levels summarize estimated foot traffic at the measurement point for that view; they are not sales, in-store counts, or city-wide totals. “Prediction” in tables means model-based future/overlay values, not the same pipeline as past-day bins.',
      '- Under “Regional context,” a dashed warning block separates canned blurbs from Summary/Tables. The “Location” and “Month” lines are typical seasonality / place flavor, not recomputed from the Summary/Tables in this prompt. Do not treat them as measured totals for the selected month or as proof of year-round “normal” congestion; keep them interpretive only.',
      '- Not included here: raw API JSON, endpoints, user actions after copy, or live refreshed values—only the snapshot at copy time.',
      '- In your final answer, favor plain language for non-specialist readers; gloss technical terms briefly when you use them.',
      '- “Service” lists only the public dashboard URL; it does not expose internal hosts or staging links.',
    ].join('\n');
  }
  return [
    '【この文書の読み方（モデル向け）】',
    '- これは会話ログではなく、高山市の歩行者カメラ計測に基づく混雑ダッシュボードから、利用者がコピーした1本の自己完結テキストです。メニュー選択・地点・期間に加え、画面データから自動生成した要約・Markdown表・地域の短文・指標の説明が順に並びます。',
    '- 推奨読み順: （1）役割と注意（2）この節（3）ダッシュボードUIの前提（4）インターフェース仕様（5）画面スナップショット内…相談→表示状態→サービス→地域文脈→イベント→要約→表→混雑指標（6）依頼。数値の主証拠はスナップショットの要約・表のみ。地域文脈の地点・時期は定性的補足。次画面の名称は（4）に従う。',
    '- 「混雑度」「要約」「表」は計測地点・選択期間の表示用集計であり、売上・店内人数・市全体の人流を表すものではない。表の「予測」は実績ビンとは別経路のモデル出力を指す。',
    '- 最終回答は、統計に詳しくない読者向けに平易語を主体とし、用語は短い言い換えを添えること。',
    '- 「サービス」には一般公開のダッシュボードURLのみが書かれている（内部ホストや開発用URLは含めない）。',
    '- 「地域文脈」は点線枠の警告の直後に続く。枠内の説明どおり「地点」「時期」は通年の定型補足であり、このプロンプト内の表・要約から再計算した数値ではない。年間を通じた高山の「普通」の混雑を断定する材料として使わず、要約・表の数値根拠と混同しないこと。',
    '- ここに含まれないもの: 生のAPIレスポンス、エンドポイントURL、コピー後のユーザー操作、リアルタイム更新後の最新値（あくまでコピー瞬間のスナップショット）。',
  ].join('\n');
}

function formatEvents(eventData, lang) {
  if (!eventData?.length) {
    return lang === 'en'
      ? 'No events registered for this month (or not loaded).'
      : 'この月の登録イベントはありません（未取得の可能性あり）。';
  }
  return eventData.map((e) => `- ${e.date}: ${e.title}`).join('\n');
}

/**
 * 【イベント】見出し付き。未登録時は高山祭などウェブ補完の指示を続ける。
 */
function formatEventsSection(lang, eventData) {
  const head = lang === 'en' ? '【Events】' : '【イベント】';
  const lines = [head, formatEvents(eventData, lang)];
  if (!eventData?.length) {
    lines.push('');
    if (lang === 'en') {
      lines.push('**Supplementary instruction:** If this calendar month may overlap the Takayama Festival (高山祭) season (commonly April and October), you may consult trustworthy web sources (e.g., official tourism portals) for exact dates. If you use them, state explicitly that the event was not listed on-screen but confirmed on the web (name the source) and **do not fabricate congestion metrics** from web text.');
    } else {
      lines.push('**補足指示：** この月が4月または10月前後で「高山祭」の開催時期に当たり得る場合は、信頼できるウェブ情報（公式観光サイト等）で日程を確認してよい。引用するときは「画面のイベント欄には未登録だが、ウェブで確認したところ〇月〇日が高山祭（出典: 〇）」のように明記し、**ウェブ文言から混雑数値を捏造しないこと**。');
    }
  }
  return lines.join('\n');
}

/**
 * 定型の地域文脈を要約・表と視覚的に分離
 */
function formatRegionalContextSection(lang, baseLines, locCtx, monthCtx) {
  if (lang === 'en') {
    return [
      '【Regional context】',
      '────────────────────────',
      '⚠️ Below: **canned** seasonal/place blurbs (typical patterns), **not** recomputed from Summary/Tables in this prompt. Keep numeric proof in Summary + Tables only.',
      '────────────────────────',
      baseLines,
      `- Location: ${locCtx}`,
      `- Month: ${monthCtx}`,
    ].join('\n');
  }
  return [
    '【地域文脈】',
    '────────────────────────',
    '⚠️ 以下は通年の一般的傾向（定型文）であり、この画面の「要約」「表」から再計算したものではありません。',
    '数値根拠は必ず「要約」「表」に置いてください（地点・時期の行を主証拠にしない）。',
    '────────────────────────',
    baseLines,
    `- 地点: ${locCtx}`,
    `- 時期: ${monthCtx}`,
  ].join('\n');
}

/**
 * カレンダー系では要約直後に実績／予測列の確認を促す
 * @param {boolean} [hasPrediction]
 */
function formatSummarySection(lang, textualSummary, action, hasPrediction) {
  const title = lang === 'en' ? '【Summary】' : '【要約】';
  const empty = lang === 'en' ? '- (none)' : '-（なし）';
  const body = textualSummary?.trim() ? textualSummary.trim() : empty;
  const parts = [title, body];
  if (action?.startsWith('cal_')) {
    const foot = lang === 'en'
      ? (hasPrediction
        ? '\n**Note:** This month mixes **already measured days** and **forward estimates** (in-app labels: actual vs forecast). Weekday averages may blend both within the same weekday. Always check the “actual days / forecast days” counts and the Notes column in the day-level table—and explain those terms plainly to the reader.'
        : '\n**Note:** In this snapshot every listed day is already measured (no forecast rows); other months may include forward estimates. Weekday averages may mix both in other months—when those columns exist, verify “actual days / forecast days” and explain in plain words.')
      : (hasPrediction
        ? '\n**注意：** この月は「すでに計測した日」と「先の日の見込み（アプリ表記: 実績／予測）」が混ざっています。曜日別の平均は、同じ曜日内でその両方を混ぜた値になり得ます。表の「うち実績／うち予測」（計測済みの日数／見込みの日数）と日別表の備考を必ず確認してください。'
        : '\n**注意：** この月の日別はすべて計測済みの日だけですが、別の月では見込みの日（予測）が表に出ることがあります。曜日別の平均は月によって計測と見込みが混ざり得るため、表の「うち実績／うち予測」列を必ず確認してください。');
    parts.push(foot);
  }
  return parts.join('\n');
}

/** 目的ラベル（i18n）→ 分析の見方1文。action からのフォールバック用にラベルも ACTION 側で保持 */
const PURPOSE_ANALYSIS_GUIDE = {
  ja: {
    店舗の定休日を検討したい: 'お店のお休みを考える際は、どの曜日が空いているかを調べ、特に客足が少ない日を見つけます。平日の比較や週ごとのパターンも参考にします。',
    商店街の定休日を検討したい: '商店街全体のお休みは、人通りが少ない曜日・時間を探し、他のお店の定休日も参考にします。地元の方と観光客の動きも大切です。',
    長期休暇のタイミングを検討したい: '長期のお休みには、連続して空いている日を探し、月の平均や前後の週末との比較、観光シーズンやイベント情報も考慮します。',
    イベントの開催日程を検討したい: 'イベント日は、人通りが多い日を見つけ、時間帯ごとの人の流れを確認します。滞在時間や曜日による参加しやすさも大切です。',
    研修のタイミングを検討したい: '研修には、人通りが少なく静かな環境が確保できる時間帯を探します。午前中や特定の曜日のパターンを見つけます。',
    イベントの効果を見る: 'イベント効果は、開催日と通常日を比較し、時間帯別の効果や翌日以降への影響も分析します。曜日や天気の影響も確認します。',
    イベントの開催時刻を検討したい: 'イベント時間は、人通りが最も多い時間帯を探し、曜日ごとの違いや天気の影響も考慮して最適な時間を提案します。',
    アルバイトのシフトを検討したい: 'アルバイトシフトは、混雑する時間帯を特定し、曜日による傾向や時間帯のピークを分析して、効率的な人員配置を提案します。',
    お店の営業時刻を検討したい: 'お店の営業時間は、人通りのピーク時間と曜日による違いを確認し、平日・休日別の需要を分析して最適な時間を提案します。',
    商店街の営業時刻を検討したい: '商店街の営業時間は、時間帯ごとの人の流れと客層の違いを考慮し、飲食・物販の需要も分析して統一時間を提案します。',
    カレンダー形式の混雑度が見たい: 'カレンダー形式では、日ごとの混雑度を見やすく表示し、曜日パターンや週による変動、天候の影響なども分析します。',
    日時形式の混雑度が見たい: '日時形式では、曜日・時間帯ごとの混雑パターンを探し、特に混む時間や空いている時間帯を見つけます。平日と休日の違いも確認します。',
    曜日形式の混雑度が見たい: '曜日×時間帯では、曜日と時間の表で混雑パターンを表示し、特に混む・空く時間帯を見つけ、平日・休日別の傾向を分析します。',
  },
  en: {
    'Consider store closing days': 'When choosing closing days, look for quieter weekdays, compare weekdays, and use week-to-week patterns as context.',
    'Consider district-wide closing days': 'For district-wide closures, find quieter weekdays/times, consider other shops’ schedules, and balance local vs visitor flows.',
    'Plan extended holidays': 'For longer closures, look for consecutive quieter days, compare month averages vs surrounding weekends, and factor seasonality and events.',
    'Plan event dates': 'For event dates, find busier days, review hour-by-hour flow, and consider dwell time and weekday participation patterns.',
    'Plan training schedule': 'For training, look for quieter hours with calmer foot traffic, such as mornings or specific weekday patterns.',
    'Evaluate event effectiveness': 'Compare event vs normal days, review hour-level effects and next-day spillover, and check weekday/weather context.',
    'Plan event timing': 'Pick peak foot-traffic hours, compare weekdays, and factor weather when proposing event timing.',
    'Adjust staff shifts': 'Identify peak congestion hours, weekday patterns, and hour-level peaks to propose staffing.',
    'Adjust business hours': 'Compare peak hours and weekday/weekend differences to propose opening hours.',
    'Adjust district hours': 'Use hour-by-hour flow and visitor mix, including food vs retail demand, to propose coordinated hours.',
    'View calendar format': 'Calendar view highlights day-level congestion, weekday patterns, weekly swings, and weather context.',
    'View hourly heatmap': 'Date×hour view finds peak vs quiet bands and weekday/weekend differences.',
    'View day-of-week heatmap': 'Weekday×hour table highlights busy vs quiet bands and weekday/weekend differences.',
  },
};

/** action → 各言語のメニューラベル（PURPOSE_ANALYSIS_GUIDE のキーと一致させる） */
const ACTION_TO_PURPOSE_LABEL = {
  cal_holiday: { ja: '店舗の定休日を検討したい', en: 'Consider store closing days' },
  cal_shoping_holiday: { ja: '商店街の定休日を検討したい', en: 'Consider district-wide closing days' },
  cal_long_holiday: { ja: '長期休暇のタイミングを検討したい', en: 'Plan extended holidays' },
  cal_event: { ja: 'イベントの開催日程を検討したい', en: 'Plan event dates' },
  cal_training: { ja: '研修のタイミングを検討したい', en: 'Plan training schedule' },
  event_effect: { ja: 'イベントの効果を見る', en: 'Evaluate event effectiveness' },
  dti_event_time: { ja: 'イベントの開催時刻を検討したい', en: 'Plan event timing' },
  wti_shift: { ja: 'アルバイトのシフトを検討したい', en: 'Adjust staff shifts' },
  dti_open_hour: { ja: 'お店の営業時刻を検討したい', en: 'Adjust business hours' },
  dti_shoping_open_hour: { ja: '商店街の営業時刻を検討したい', en: 'Adjust district hours' },
  cal_cog: { ja: 'カレンダー形式の混雑度が見たい', en: 'View calendar format' },
  dti_cog: { ja: '日時形式の混雑度が見たい', en: 'View hourly heatmap' },
  wti_cog: { ja: '曜日形式の混雑度が見たい', en: 'View day-of-week heatmap' },
};

/**
 * 現在の相談ラベルに対応する「分析のヒント」1文（表は出さない）
 * @returns {string}
 */
function getPurposeAnalysisAdvice(lang, purposeLabel, action) {
  const L = lang === 'en' ? 'en' : 'ja';
  const map = PURPOSE_ANALYSIS_GUIDE[L];
  const label = String(purposeLabel || '').trim();
  if (label && map[label]) return map[label];
  if (action && ACTION_TO_PURPOSE_LABEL[action]) {
    const key = ACTION_TO_PURPOSE_LABEL[action][L];
    if (key && map[key]) return map[key];
  }
  return '';
}

/**
 * 相談ラベルと分析ヒントを1ブロックにまとめる（目的の二重表記を避ける）
 */
function formatConsultationAndHint(lang, purposeLabel, action) {
  const label = String(purposeLabel || '').trim();
  const advice = getPurposeAnalysisAdvice(lang, purposeLabel, action);
  if (lang === 'en') {
    const lines = ['【Consultation & how to read the data】', label];
    if (advice) lines.push(`Hint: ${advice}`);
    return lines.join('\n');
  }
  const lines = ['【相談と分析のヒント】', label];
  if (advice) lines.push(advice);
  return lines.join('\n');
}

/** @param {string} action */
function describePrimaryVisualization(action, lang) {
  if (!action) {
    return lang === 'en' ? 'View tied to the selected menu item' : 'メニューで選択中のビュー';
  }
  if (action === 'today_details') {
    return lang === 'en' ? 'Learn about today (multi-view)' : '今日について詳しく知りたい（マルチビュー）';
  }
  if (action === 'event_effect') {
    return lang === 'en' ? 'Evaluate event effectiveness (comparison view)' : 'イベントの効果を見る（比較ビュー）';
  }
  if (action.startsWith('cal_')) {
    return lang === 'en'
      ? 'Calendar-format congestion (monthly day cells; menu label: View calendar format)'
      : 'カレンダー形式の混雑度（月内・日次の格子。メニュー表記どおり）';
  }
  if (action.startsWith('dti_')) {
    return lang === 'en'
      ? 'Date × hour congestion (menu label: View hourly heatmap)'
      : '日時形式の混雑度が見たい（日付×時間帯。メニュー表記どおり）';
  }
  if (action.startsWith('wti_')) {
    return lang === 'en'
      ? 'Weekday × hour congestion (menu label: View day-of-week heatmap)'
      : '曜日形式の混雑度が見たい（曜日×時間帯。メニュー表記どおり）';
  }
  if (action === 'month_trend') {
    return lang === 'en' ? 'View monthly trends' : '月ごとの傾向を見たい';
  }
  if (action === 'week_trend') {
    return lang === 'en' ? 'View weekly trends' : '週ごとの傾向を見たい';
  }
  if (action === 'year_trend') {
    return lang === 'en' ? 'View yearly trends' : '年別の傾向を見たい';
  }
  if (action.startsWith('foreigners_')) {
    return lang === 'en' ? 'Foreign visitor distribution view' : '外国人の分布を見たい';
  }
  return lang === 'en' ? 'Visualization for the selected action' : '選択中アクションに対応する可視化';
}

/**
 * 目的ベース／グラフベースUIの前提（利用者向けの操作イメージ）
 */
function dashboardDesignPremiseNote(lang) {
  if (lang === 'en') {
    return [
      '【Dashboard UI premise】',
      '- Many tourism merchants want data but struggle to connect charts to decisions; classic dashboards often force “pick place/date first, then hunt a chart,” which widens the gap for non-specialists.',
      '- This app offers two entry styles: (1) Action-first: choose a business goal first; the matching visualization opens. (2) Data-first: choose a chart type first, then set location/period.',
      '- Typical mappings use the app’s menu wording: closing/holiday/event/training goals → calendar-format congestion; event time / opening hours goals → date×hour view (“View hourly heatmap”); staffing shifts → weekday×hour view (“View day-of-week heatmap”); event effectiveness → comparison view; “today” → Learn about today.',
      '- Congestion is mainly a ~20-level relative scale for intuitive comparison, not a claim about exact footfall.',
    ].join('\n');
  }
  return [
    '【ダッシュボードUIの前提】',
    '- 観光地の商店街ではデータに関心があっても「どのグラフを見れば業務判断に使えるかわからない」ことが多く、従来型は「場所・日付を先に決めてからグラフを探す」流れになりやすい。',
    '- 本アプリには（1）目的ベース: 業務上の目的を先に選ぶと対応する可視化が開く。（2）グラフベース: 可視化の形式を先に選び、地点・期間を指定するの二系統がある。',
    '- 対応の例（メニュー表記どおり）: 定休日・商店街休日・連休・イベント日・研修日の検討 → 「カレンダー形式の混雑度が見たい」と同種のカレンダー表示。イベント時刻・営業時間の検討 → 「日時形式の混雑度が見たい」。シフト検討 → 「曜日形式の混雑度が見たい」。イベント効果 → 「イベントの効果を見る」。今日の様子 → 「今日について詳しく知りたい」。傾向 → 「週ごとの傾向を見たい」「月ごとの傾向を見たい」「年別の傾向を見たい」。',
    '- 混雑は主に20段階前後の相対指標で、絶対通行量の確定値ではない。',
  ].join('\n');
}

/**
 * メニュー文言とモード切替（インターフェース仕様として固定）
 */
function formatInterfaceSpecBlock(lang) {
  if (lang === 'en') {
    return [
      '【Interface specification (avoid mis-navigation)】',
      '- Toggle **Action-First** (/purpose) vs **Data-First** (/function) from the header. When you tell the user what to open next, use the exact menu labels below—do not invent labels like “weekday×time heatmap” without the in-app wording.',
      '- Data-First congestion menu labels: **View calendar format** (monthly day grid), **View hourly heatmap** (date×hour bands in this product), **View day-of-week heatmap** (weekday×hour bands).',
      '- Example (user is on Action-First calendar): “Click **Data-First** in the header, confirm location and year/month, then pick **View day-of-week heatmap** for weekday×hour patterns, or **View hourly heatmap** for date×hour patterns in the selected month.” Pick one path and explain why.',
      '- Action-First business-goal items (e.g., district closing days) open the paired visualization described in the UI premise block; do not rename those flows with generic BI jargon.',
    ].join('\n');
  }
  return [
    '【インターフェース仕様（誤案内防止）】',
    '- ヘッダーで「目的ベースへ」「グラフベースへ」を切り替える（URL は `/purpose` と `/function`）。ユーザーへ次に開く画面を書くときは、画面メニューに出ている**文言をそのまま**使い、「曜日×時間ヒートマップ」などアプリに無い略称だけで指示しない。',
    '- グラフベースで選ぶ混雑ビューの正式名称: 「カレンダー形式の混雑度が見たい」（月内の日次格子）、「日時形式の混雑度が見たい」（日付ごとに時間帯）、「曜日形式の混雑度が見たい」（曜日ごとに時間帯）。',
    '- 操作例（いま目的ベースでカレンダーを見ている場合）: 「ヘッダーの『グラフベースへ』を押す → 地点と年・月を確認する → メニューから『曜日形式の混雑度が見たい』（曜日×時間帯）か『日時形式の混雑度が見たい』（日付×時間帯）を選ぶ」。勧める側を相談内容に合わせて一文で区別する。',
    '- 目的ベースのメニュー（例:「商店街の定休日を検討したい」）は、【ダッシュボードUIの前提】に書いたとおり対応する可視化が開く。別名の画面を新設しない。',
  ].join('\n');
}

/**
 * @param {'purpose'|'function'|'other'} uiMode
 */
function formatScreenSnapshot(lang, uiMode, action, purposeLabel, locationLabel, periodLabel) {
  const viz = describePrimaryVisualization(action, lang);
  const modeLine = (() => {
    if (lang === 'en') {
      if (uiMode === 'purpose') {
        return 'UI mode: Action-first (/purpose). The user picked a business goal first; the chart below matches that intent.';
      }
      if (uiMode === 'function') {
        return 'UI mode: Data-first (/function). The user picked a chart type first; the label reflects “what to view,” not necessarily a business decision wording.';
      }
      return 'UI mode: not determined from the URL path; interpret labels cautiously.';
    }
    if (uiMode === 'purpose') {
      return 'UIモード: 目的ベース（/purpose）。利用者が先に業務目的を選び、その目的に対応するグラフが開いている。';
    }
    if (uiMode === 'function') {
      return 'UIモード: グラフベース（/function）。利用者が先にグラフ形式を選んでいるため、メニュー表示は「何を見たいか」に寄り、業務目的の文章とは一致しない場合がある。';
    }
    return 'UIモード: URLから purpose / function を判定できなかった。ラベルは文脈に合わせて解釈すること。';
  })();
  if (lang === 'en') {
    return [
      '【What is on screen (snapshot at copy time)】',
      '- This prompt embeds a snapshot of the user’s current dashboard state. It is not a live sync; it reflects settings at the moment of copy.',
      `- ${modeLine}`,
      `- Primary visualization: ${viz}`,
      `- Menu selection (goal or chart intent): ${purposeLabel || '—'}`,
      `- Location: ${locationLabel}`,
      `- Period: ${periodLabel}`,
    ].join('\n');
  }
  return [
    '【いま表示している画面（コピー時点のスナップショット）】',
    '- 以下の要約・表は、利用者がこのダッシュボードで閲覧している画面の設定を自動で埋め込んだものです。リアルタイム同期ではなく、コピーした瞬間の状態です。',
    `- ${modeLine}`,
    `- 主たる可視化: ${viz}`,
    `- メニューで選択中のラベル（目的ベースでは業務目的、グラフベースでは「見たいグラフ」の意図）: ${purposeLabel || '—'}`,
    `- 地点: ${locationLabel}`,
    `- 期間: ${periodLabel}`,
  ].join('\n');
}

/**
 * 混雑指標の読み方を短文で埋め込み、数値の過剰解釈を抑える。
 */
function congestionMethodologyNote(lang) {
  if (lang === 'en') {
    return [
      '【Congestion metric】',
      '【Meaning for merchants】',
      '- Levels describe “how busy the street looks at the measurement point,” not revenue or how many people entered a specific shop. A quiet street day can still be a good sales day for a given store—urge the reader to cross-check with their own sales and regulars when relevant.',
      '【How to read the numbers】',
      '- Levels estimate pedestrian activity (higher = busier), not sales or in-store counts; use for relative comparison within this view.',
      '- Actual days: integer bins from location thresholds and that month’s daily pattern. Forecast days: hourly model (1–20) may be summarized to a daily peak in active hours (~7–21); cells marked “prediction” follow that path.',
      '- When citing numbers, explicitly tag each date or weekday aggregate as actual vs forecast. Weekday averages may mix both within the same weekday row—use the “actual / forecast day counts” columns and the “actual-only” comparison table when present.',
      '- Averages with decimals come from averaging discrete day levels; they are not precise physical units.',
    ].join('\n');
  }
  return [
    '【混雑指標の前提】',
    '【事業者向けの意味付け】',
    '- 混雑度は「通りの人出のしやすさ」のレベルであり、売上や店内の人数そのものではありません。通りが空いている日は休み候補にしやすい一方で、「通りは普通でも自店の売上が良い日」もあり得るため、必要に応じて自店の売上・常連の動きとの照合を促してください。',
    '- モデルがユーザーに返す文章では、「実績」「予測」などアプリ用語を出す場合は必ず一言の言い換えを添え、統計に不慣れな人が読んでも意味が取れるようにする。',
    '【データの見方】',
    '- 混雑度は計測地点の歩行者推計の離散レベル（大きいほど混みやすい）で、同一画面内での比較・傾向把握が主目的です。',
    '- 実績は地点閾値と月内の日次パターンから整数化。予測は時間別モデル（1〜20）を活動時間帯のピーク等で日単位に代表することがあります（「予測」表示のセル）。',
    '- 数値を根拠に述べるときは、日付・曜日集計ごとに「実績」か「予測」かを必ず明示する。曜日別の平均は同一曜日内で実績と予測を混ぜた値になり得るため、表の「うち実績／うち予測」列と「実績日のみ」の比較表を対照し、予測を確定通行量として扱わない。',
    '- 曜日平均の小数は、複数日の整数レベルを平均した表示であり、精密な物理量ではありません。',
  ].join('\n');
}

function outputInstructions(action, lang) {
  if (lang === 'en') {
    const ex = action.startsWith('cal_')
      ? 'days/holidays where staying open (or closing) needs extra judgment'
      : action.startsWith('wti_') || action.startsWith('dti_')
        ? 'time bands or dates needing extra staffing/hours adjustments'
        : 'exception buckets or seasons needing caution';
    return [
      'Please answer in English, structured as:',
      '1) One-sentence conclusion (prefer ending with a cautious stance such as “…is a reasonable reading given the data.”)',
      '2) Exactly three evidence points (bullets or short sentences) tied primarily to Summary + Tables. Each must clearly cite numbers/dates, clearly distinguish measured days vs forward estimates, and give a plain-language “so what” for the shop—without requiring (a)/(b)/(c) labels. Do not use “Location/Month” blurbs as numeric evidence.',
      '   **Example (pattern only):** Thursdays this month averaged congestion 7.4 across five **already measured** days (not estimates for future days)—the lowest and steadiest weekdays—so they are a practical anchor for closing-day ideas.',
      `3) Exceptions to watch (${ex}) — use 2–3 sub-bullets if helpful.`,
      '4) Two actionable options (different angles). For **each** option, include in order: (i) which weekday/time band (with data citation), (ii) what to verify in the shop (staffing, neighbors, local events, etc.), (iii) the next dashboard screen to open using **exact menu labels** and the Action-First / Data-First toggle per the Interface specification block. Keep each option within about three short sentences.',
      '5) Uncertainty — 2–3 sentences naming concrete unknowns (what this dataset cannot prove).',
      'If you suggest opening another screen, follow **Interface specification** verbatim—no invented screen names.',
    ].join('\n');
  }

  const ex = action.startsWith('cal_')
    ? '定休日でも開ける／閉める判断が難しい日'
    : action.startsWith('wti_') || action.startsWith('dti_')
      ? '人手や営業時間を変えるべき時間帯・日付'
      : '注意すべき例外区分や季節要因';

  return [
    '次の形式で日本語で回答してください:',
    '1. 結論（1文。断定しすぎず「〜と考えられます」「〜が妥当そうです」など推量が伝わる語尾を推奨）',
    '2. 根拠（ちょうど3点。主に「要約」と「表」。各点で①数値や日付、②その数値が「この月ですでに計測された日（アプリでは実績）」か「先の日の見込み（予測）」のどちらに基づくか、③店の判断にどう効くか、が**データに不慣れな人にも**読み取れるように書く。(a)(b)(c)の記号は使わなくてよい。地域文脈の地点・時期は主証拠にしない）',
    '   **例（書き方の型）:** 木曜はこの月5日分、すでに計測された日だけを平均すると混雑度が7.4で、どの曜日よりも通りが空きやすい。休みの候補の軸にしやすい。',
    `3. 例外的に注意すべきこと（${ex}）。必要なら箇条書き2〜3個`,
    '4. 実行案を2つ（異なる切り口）。各案について順に: (1) どの曜日・時間帯の候補か（データ根拠を添える）(2) 決める前に店で確認すべきこと（他店・スタッフ・地域行事など）(3) ダッシュボードで次に開く画面（【インターフェース仕様】のメニュー文言のまま＋「目的ベースへ／グラフベースへ」の切替を具体的に）。各案はおおむね3文以内を目安',
    '5. この助言の不確実性（2〜3文。何がデータから言えないかを具体的に）',
    '※ 次画面の案内は【インターフェース仕様】に従い、「要約・表にない数値を作らない」「地域文脈を数値の主証拠にしない」ことと矛盾させないこと。',
  ].join('\n');
}

/**
 * @param {{
 *   lang: 'ja'|'en',
 *   purposeLabel: string,
 *   locationLabel: string,
 *   periodLabel: string,
 *   locationKey: string,
 *   monthNum: number|string,
 *   action: string,
 *   eventData: Array<{date:string,title:string}>,
 *   textualSummary: string,
 *   dataTables: Array<{title:string, markdownTable:string}>,
 *   userNotes?: string,
 *   uiMode?: 'purpose'|'function'|'other',
 *   hasPrediction?: boolean,
 * }} input
 */
export function buildAiConsultPrompt(input) {
  const {
    lang,
    purposeLabel,
    locationLabel,
    periodLabel,
    locationKey,
    monthNum,
    action,
    eventData,
    textualSummary,
    dataTables,
    userNotes,
    uiMode = 'other',
    hasPrediction = false,
  } = input;

  const baseLines = getTakayamaBase(lang).map((l) => `- ${l}`).join('\n');
  const locCtx = getLocationContext(locationKey, lang);
  const monthCtx = getMonthlyContext(monthNum, lang);
  const eventsSection = formatEventsSection(lang, eventData);
  const regionalSection = formatRegionalContextSection(lang, baseLines, locCtx, monthCtx);
  const summarySection = formatSummarySection(lang, textualSummary, action, hasPrediction);

  const tablesBlock = (dataTables || [])
    .filter((t) => t.markdownTable)
    .map((t) => `【${t.title}】\n${t.markdownTable}`)
    .join('\n\n');

  const methodologyBlock = congestionMethodologyNote(lang);
  const consultationBlock = formatConsultationAndHint(lang, purposeLabel, action);
  const screenBlock = formatScreenSnapshot(
    lang,
    uiMode,
    action,
    purposeLabel,
    locationLabel,
    periodLabel,
  );
  const uiPremiseBlock = dashboardDesignPremiseNote(lang);
  const interfaceSpecBlock = formatInterfaceSpecBlock(lang);
  const documentGuideBlock = formatPromptDocumentGuide(lang);
  const merchantGuideBlock = buildMerchantGuideBlock(lang);
  const snapshotHeader = lang === 'en'
    ? '【Screen snapshot (variable at copy time)】\n- Everything below this header is embedded from the dashboard at copy time; it is not live-synced.'
    : '【画面スナップショット（コピー時点で可変）】\n- この見出しより下は、その瞬間のダッシュボードから埋め込んだ可変データです（リアルタイム同期ではありません）。';

  const publicServiceBlock = formatPublicServiceLine(lang);

  const notesBlock = userNotes?.trim()
    ? (lang === 'en'
      ? `【Notes】\n${userNotes.trim()}`
      : `【補足】\n${userNotes.trim()}`)
    : '';

  if (lang === 'en') {
    return [
      merchantGuideBlock,
      '',
      documentGuideBlock,
      '',
      uiPremiseBlock,
      '',
      interfaceSpecBlock,
      '',
      snapshotHeader,
      '',
      consultationBlock,
      '',
      screenBlock,
      '',
      publicServiceBlock,
      '',
      regionalSection,
      '',
      eventsSection,
      '',
      summarySection,
      '',
      '【Tables】',
      tablesBlock || '- (none)',
      '',
      methodologyBlock,
      '',
      notesBlock,
      '',
      '【Instructions】',
      outputInstructions(action, lang),
    ].filter(Boolean).join('\n');
  }

  return [
    merchantGuideBlock,
    '',
    documentGuideBlock,
    '',
    uiPremiseBlock,
    '',
    interfaceSpecBlock,
    '',
    snapshotHeader,
    '',
    consultationBlock,
    '',
    screenBlock,
    '',
    publicServiceBlock,
    '',
    regionalSection,
    '',
    eventsSection,
    '',
    summarySection,
    '',
    '【表】',
    tablesBlock || '-（なし）',
    '',
    methodologyBlock,
    '',
    notesBlock,
    '',
    '【依頼】',
    outputInstructions(action, lang),
  ].filter(Boolean).join('\n');
}
